import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { Badge } from "../badge";
import MetodoPagoField from "../shared/MetodoPagoField";
import { Alert, AlertTitle, AlertDescription, AlertActions } from "../alert";
import { Button } from "../button";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { esSoloLectura } from "../../utils/roles";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import { registrarAdquisicion } from "../../utils/inventario";
import { parseDateInputToTimestamp, formatCalendarDate, todayInputValue } from "../../utils/dates";
import { totalCostos, CATEGORIAS_COSTO } from "../../utils/costos";
import {
  esDescuento,
  esCobrable,
  esDelVenue,
  esRenta,
  incluyeDe,
  rentasQueIncluyen,
  proveedorDe,
  adquisicionDe,
  unidadDe,
  llevaInventario,
  estaClasificado,
  describeDimensiones,
  precioDe,
  precioDeVariante,
  tieneCosto,
  variantesDe,
  costoProveedorDe,
  costoProveedorDeVariante,
  ingresosPorItem,
  recuperacionItem,
} from "../../utils/catalogo";
import { formDesdeServicio, validarForm, guardarServicio } from "../../utils/servicios";
import ServicioForm from "./ServicioForm";
import ServicioInventario from "./ServicioInventario";
import ServicioReservas from "./ServicioReservas";
import HistorialPrecios from "../shared/HistorialPrecios";
import {
  ChevronLeftIcon,
  PencilSquareIcon,
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon,
  RectangleStackIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  return "$" + n.toLocaleString("es-MX", {
    minimumFractionDigits: n % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

// Color de los chips de describeDimensiones: quién lo pone y si tiene piezas.
const CHIP_COLOR = {
  venue: "zinc",
  proveedor: "blue",
  piezas: "emerald",
  renta: "amber",
  incluye: "zinc",
};

// Detalle de un servicio del catálogo: qué se cobra, quién lo da, cuánto ha
// costado, si ya se pagó solo, y su inventario cuando lleva.
const ServicioDetallePage = () => {
  const { servicioId } = useParams();
  const navigate = useNavigate();
  const {
    selectedVenueId,
    costos,
    conceptos,
    proveedores,
    events,
    calendarTimezone,
    metodosPagoConDefaults: metodosPago,
  } = useVenue();
  const { userInfo, user } = useAuth();
  // Inversionista (P-FRA-13): la ficha completa con precios, costos e
  // historiales, sin editar, archivar, registrar inversión ni gastos.
  const soloLectura = esSoloLectura(userInfo);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [tab, setTab] = useState("resumen");
  const [inversion, setInversion] = useState(null);
  // Captura de gasto ligado al servicio (mantenimiento, reparación). null =
  // cerrado; el objeto es el borrador del formulario.
  const [gasto, setGasto] = useState(null);
  const [savingGasto, setSavingGasto] = useState(false);

  useEffect(() => {
    if (!selectedVenueId || !servicioId) return;
    const unsub = onSnapshot(
      doc(db, "venues", selectedVenueId, "conceptos", servicioId),
      (snap) => {
        setItem(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      (err) => {
        console.error("Catálogo snapshot error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [selectedVenueId, servicioId]);

  // Todo costo atribuido a un servicio lo referencia con `conceptoId`, sea
  // del inventario, de la cotización o capturado a mano.
  const costosDelItem = useMemo(
    () =>
      (costos || []).filter(
        (c) => c.conceptoId === servicioId
      ),
    [costos, servicioId]
  );
  const costosOrdenados = useMemo(
    () =>
      [...costosDelItem].sort((a, b) => {
        const av = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha || 0);
        const bv = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha || 0);
        return bv - av;
      }),
    [costosDelItem]
  );

  const eventsById = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => { map[e.id] = e; });
    return map;
  }, [events]);

  const proveedoresPorId = useMemo(() => {
    const map = {};
    (proveedores || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [proveedores]);

  // Para validar y filtrar lo que una renta incluye al guardarla.
  const conceptosPorId = useMemo(() => {
    const map = {};
    (conceptos || []).forEach((c) => { if (c?.id) map[c.id] = c; });
    return map;
  }, [conceptos]);

  // En qué rentas va incluido (si es un hijo) y qué trae (si es una renta).
  const padres = useMemo(() => rentasQueIncluyen(conceptos, servicioId), [conceptos, servicioId]);
  const traeIncluidos = useMemo(
    () =>
      esRenta(item)
        ? incluyeDe(item)
            .map(({ conceptoId, cantidad }) => ({
              concepto: conceptosPorId[conceptoId] || null,
              conceptoId,
              cantidad,
            }))
            .filter((x) => x.concepto)
        : [],
    [item, conceptosPorId]
  );

  const startEdit = () => {
    setForm(formDesdeServicio(item));
    setIsEditing(true);
  };

  const handleSave = async () => {
    const error = validarForm(form, { conceptosPorId, itemId: servicioId });
    if (error) {
      showAlert(error, "error");
      return;
    }
    setSaving(true);
    try {
      await guardarServicio({
        venueId: selectedVenueId,
        itemId: servicioId,
        actual: item,
        form,
        user,
        userInfo,
        proveedoresPorId,
        conceptosPorId,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.SERVICIO_EDIT,
          entity: "servicio",
          entityId: servicioId,
          entityName: form.nombre.trim(),
          details: {
            proveedorId: form.deProveedor ? form.proveedorId : null,
            itemizado: !!form.itemizado,
            unidad: form.unidad,
          },
        },
        userInfo,
        user
      );
      setIsEditing(false);
      showAlert("Servicio actualizado", "success");
    } catch (err) {
      showAlert(`No se pudo guardar. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleArchivado = async () => {
    const archivar = !item.archivedAt;
    try {
      await updateDoc(doc(db, "venues", selectedVenueId, "conceptos", servicioId), {
        archivedAt: archivar ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logBitacora(
        selectedVenueId,
        {
          action: archivar
            ? BITACORA_ACTIONS.SERVICIO_ARCHIVE
            : BITACORA_ACTIONS.SERVICIO_UNARCHIVE,
          entity: "servicio",
          entityId: servicioId,
          entityName: item.nombre || "",
          details: {},
        },
        userInfo,
        user
      );
      showAlert(archivar ? "Servicio archivado" : "Servicio restaurado", "success");
    } catch (err) {
      showAlert(`No se pudo actualizar. [${err.message}]`, "error");
    }
  };

  // Capturar la inversión después: al dar de alta se pudo haber omitido, y
  // la factura aparece la semana siguiente.
  const handleGuardarInversion = async () => {
    const monto = Number(normalizeMoneyInput(inversion.monto)) || 0;
    if (monto <= 0) {
      showAlert("Ingresa cuánto costó.", "error");
      return;
    }
    try {
      await registrarAdquisicion({
        venueId: selectedVenueId,
        itemId: servicioId,
        itemNombre: item.nombre,
        monto,
        fecha: parseDateInputToTimestamp(inversion.fecha, calendarTimezone),
        registrarGasto: inversion.registrarGasto,
        user,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.SERVICIO_EDIT,
          entity: "servicio",
          entityId: servicioId,
          entityName: item.nombre || "",
          details: { adquisicion: monto },
        },
        userInfo,
        user
      );
      setInversion(null);
      showAlert("Inversión registrada", "success");
    } catch (err) {
      showAlert(`No se pudo guardar. [${err.message}]`, "error");
    }
  };

  // Gasto general ligado al servicio (mantenimiento, reparación, limpieza).
  // Sale como costo de caja sin evento; al llevar `conceptoId` cuenta como
  // costo de uso en la recuperación de la inversión.
  const handleRegistrarGasto = async () => {
    if (!gasto.concepto.trim()) {
      showAlert("Describe el gasto.", "error");
      return;
    }
    const monto = Number(normalizeMoneyInput(gasto.monto)) || 0;
    if (monto <= 0) {
      showAlert("Ingresa un monto válido mayor a 0.", "error");
      return;
    }
    const fecha = parseDateInputToTimestamp(gasto.fecha, calendarTimezone);
    if (!fecha) {
      showAlert("Fecha inválida.", "error");
      return;
    }
    setSavingGasto(true);
    try {
      const docRef = await addDoc(collection(db, "venues", selectedVenueId, "costos"), {
        eventId: null,
        concepto: gasto.concepto.trim(),
        categoria: gasto.categoria,
        proveedorId: null,
        proveedorNombre: null,
        conceptoId: item.id,
        conceptoNombre: item.nombre || "",
        monto,
        montoOriginal: null,
        fecha,
        estado: gasto.estado,
        fechaPago: gasto.estado === "pagado" ? fecha : null,
        origen: "manual",
        metodo: gasto.estado === "pagado" ? gasto.metodo || null : null,
        notas: null,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        updatedAt: null,
        updatedBy: null,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.COSTO_CREATE,
          entity: "costo",
          entityId: docRef.id,
          entityName: gasto.concepto.trim(),
          details: { monto, categoria: gasto.categoria, conceptoId: item.id },
        },
        userInfo,
        user
      );
      setGasto(null);
      showAlert("Gasto registrado", "success");
    } catch (err) {
      showAlert(`No se pudo registrar. [${err.message}]`, "error");
    } finally {
      setSavingGasto(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, "venues", selectedVenueId, "conceptos", servicioId));
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.SERVICIO_DELETE,
          entity: "servicio",
          entityId: servicioId,
          entityName: item.nombre || "",
          details: {},
        },
        userInfo,
        user
      );
      showAlert("Servicio eliminado", "success");
      navigate("/dashboard/servicios");
    } catch (err) {
      showAlert(`No se pudo eliminar. [${err.message}]`, "error");
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-zinc-400">Cargando...</div>;
  }
  if (!item) {
    return (
      <div className="p-8 text-center text-zinc-500">
        No se encontró este servicio en el catálogo.
      </div>
    );
  }

  const proveedorId = proveedorDe(item);
  const proveedor = proveedorId ? proveedoresPorId[proveedorId] || null : null;
  const proveedorNombre = proveedor?.nombre || item.proveedorNombre || "Proveedor";
  const chips = esDescuento(item) ? [] : describeDimensiones(item, { proveedoresPorId });
  const revisar = !esDescuento(item) && !estaClasificado(item);
  const variantes = variantesDe(item);
  const adquisicion = adquisicionDe(item);
  // Inventario y reservas van juntos: solo un servicio con piezas tiene algo
  // que asignar a un evento (mismo criterio que la sección Servicios del
  // evento).
  const conInventario = llevaInventario(item);
  const tabs = [
    { id: "resumen", label: "Resumen" },
    ...(conInventario ? [{ id: "inventario", label: "Inventario" }] : []),
    ...(conInventario ? [{ id: "reservas", label: "Reservas" }] : []),
  ];
  const tabActivo = tabs.some((t) => t.id === tab) ? tab : "resumen";
  const ingresos = ingresosPorItem({ events, itemId: servicioId, conceptos });
  const ingresosPorVariante = Object.values(ingresos.variantes || {}).sort(
    (a, b) => b.total - a.total
  );
  const recuperacion = recuperacionItem({ item, ingresos, costos: costosDelItem });
  const totalGastado = totalCostos(costosDelItem);
  const pendiente = totalCostos(costosDelItem, { estado: "por_pagar" });
  const totalCobrado = Number(ingresos.total) || 0;
  const utilidad = totalCobrado - totalGastado;
  // Para descuentos las métricas de arriba no dicen nada (no generan costos
  // y sus líneas son negativas, así que ingresosPorItem las ignora): lo que
  // importa es cuántas líneas de cotización lo aplicaron.
  const vecesAplicado = esDescuento(item)
    ? (events || []).reduce((n, ev) => {
        if (!ev || ev.status === "cancelado") return n;
        const lineas = Array.isArray(ev.desglose) ? ev.desglose : [];
        return n + lineas.filter((l) => l?.conceptoId === servicioId).length;
      }, 0)
    : 0;

  // "Cobras": el precio por unidad.
  const cobro = esCobrable(item) && precioDe(item) > 0 ? precioDe(item) : null;
  // "Te cuesta": lo que el proveedor te cobra por una unidad.
  const costoProveedor = proveedorId && tieneCosto(item) ? costoProveedorDe(item) : null;

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  const kpiCols = esDescuento(item)
    ? "grid-cols-2"
    : proveedorId
    ? "grid-cols-2 sm:grid-cols-5"
    : "grid-cols-2 sm:grid-cols-4";

  return (
    <div className="max-w-5xl mx-auto">
      <button
        type="button"
        onClick={() => navigate("/dashboard/servicios")}
        className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 mb-4"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Servicios
      </button>

      {/* Cabecera */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm mb-5 overflow-hidden">
        <div className="flex items-start gap-3 p-5">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: ACCENT + "1a" }}
          >
            <RectangleStackIcon className="h-5 w-5" style={{ color: ACCENT }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-zinc-900 dark:text-white truncate">
                {item.nombre}
              </h1>
              {esDescuento(item) && <Badge color="red">Descuento</Badge>}
              {chips.map((chip) => (
                <Badge key={chip.tipo} color={CHIP_COLOR[chip.tipo] || "zinc"}>
                  {chip.label}
                </Badge>
              ))}
              {revisar && <Badge color="amber">Sin revisar</Badge>}
              {!esRenta(item) && !esCobrable(item) && (
                padres.length === 0 ? (
                  <Badge color="amber">Incluido, sin renta</Badge>
                ) : (
                  <Badge color="zinc">
                    {padres.length === 1
                      ? `Incluido en ${padres[0].nombre}`
                      : `Incluido en ${padres.length} rentas`}
                  </Badge>
                )
              )}
              {variantes.length > 0 && (
                <Badge color="indigo">
                  {variantes.length} {variantes.length === 1 ? "variante" : "variantes"}
                </Badge>
              )}
              {item.archivedAt && <Badge color="zinc">Archivado</Badge>}
            </div>
            <div className="text-sm text-zinc-500">
              {esDescuento(item)
                ? "Descuento del catálogo"
                : `Se cobra por ${unidadDe(item)}`}
              {item.descripcion ? ` · ${item.descripcion}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!isEditing && !soloLectura && (
              <button
                type="button"
                onClick={startEdit}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition flex items-center gap-1.5"
              >
                <PencilSquareIcon className="h-3.5 w-3.5" />
                Editar
              </button>
            )}
            {!soloLectura && (
            <button
              type="button"
              onClick={toggleArchivado}
              className="p-2 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition"
              title={item.archivedAt ? "Restaurar" : "Archivar"}
            >
              {item.archivedAt ? (
                <ArchiveBoxArrowDownIcon className="h-4 w-4" />
              ) : (
                <ArchiveBoxIcon className="h-4 w-4" />
              )}
            </button>
            )}
          </div>
        </div>
        {/* La historia completa de una mirada: qué cobra, qué te cuesta,
            cuánto ha generado, cuánto ha costado y qué deja. */}
        <div
          className={`border-t border-zinc-100 dark:border-zinc-700/40 grid ${kpiCols} divide-x divide-zinc-100 dark:divide-zinc-700/40 text-center`}
        >
          <div className="px-3 py-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
              {esDescuento(item) ? "Descuento" : "Cobras"}
            </div>
            <div className="mt-0.5 text-sm font-bold text-zinc-900 dark:text-white">
              {!esCobrable(item) ? (
                <span className="text-sm font-medium text-zinc-400">Incluido en la renta</span>
              ) : cobro ? (
                fmtMoney(cobro)
              ) : (
                "—"
              )}
            </div>
            {esCobrable(item) && cobro && (
              <div className="text-xs text-zinc-400">
                {esDescuento(item) ? `por ${unidadDe(item)} (se resta)` : `por ${unidadDe(item)}`}
              </div>
            )}
          </div>
          {!esDescuento(item) && proveedorId && (
            <div className="px-3 py-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                Te cuesta
              </div>
              <div className="mt-0.5 text-sm font-bold text-red-600">
                {costoProveedor ? fmtMoney(costoProveedor) : "—"}
              </div>
              <div className="text-xs text-zinc-400 truncate">
                {costoProveedor ? `por ${unidadDe(item)} · ` : "sin costo capturado · "}
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/proveedores/${proveedorId}`)}
                  className="hover:underline font-medium"
                  style={{ color: ACCENT }}
                >
                  {proveedorNombre}
                </button>
              </div>
            </div>
          )}
          {!esDescuento(item) && (
            <div className="px-3 py-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                Cobrado
              </div>
              <div className="mt-0.5 text-sm font-bold text-zinc-900 dark:text-white">
                {totalCobrado > 0 ? fmtMoney(totalCobrado) : "—"}
              </div>
              {ingresos.eventos.length > 0 && (
                <div className="text-xs text-zinc-400">
                  en {ingresos.eventos.length}{" "}
                  {ingresos.eventos.length === 1 ? "evento" : "eventos"}
                </div>
              )}
            </div>
          )}
          {!esDescuento(item) && (
            <div className="px-3 py-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                Costo acumulado
              </div>
              <div className="mt-0.5 text-sm font-bold text-red-600">
                {fmtMoney(totalGastado)}
              </div>
              {pendiente > 0 && (
                <div className="text-xs text-amber-600">
                  {fmtMoney(pendiente)} por pagar
                </div>
              )}
            </div>
          )}
          {!esDescuento(item) && (
            <div className="px-3 py-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                Utilidad
              </div>
              <div
                className={`mt-0.5 text-sm font-bold ${
                  utilidad >= 0 ? "text-emerald-600" : "text-red-600"
                }`}
              >
                {totalCobrado > 0 || totalGastado > 0 ? fmtMoney(utilidad) : "—"}
              </div>
              <div className="text-xs text-zinc-400">cobrado − costos</div>
            </div>
          )}
          {esDescuento(item) && (
            <div className="px-3 py-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">
                Veces aplicado
              </div>
              <div className="mt-0.5 text-sm font-bold text-zinc-900 dark:text-white">
                {vecesAplicado}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Qué trae la renta. Va arriba de las pestañas porque en una renta es EL
          dato: el precio ya está en los KPIs y lo demás de la ficha (costos,
          recuperación) casi nunca aplica. */}
      {esRenta(item) && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Qué incluye
            </div>
            <span className="text-xs text-zinc-400">
              {traeIncluidos.length === 0
                ? "nada configurado"
                : `${traeIncluidos.length} ${traeIncluidos.length === 1 ? "servicio" : "servicios"}`}
            </span>
            {!soloLectura && (
            <button
              type="button"
              onClick={startEdit}
              className="ml-auto text-xs font-semibold hover:underline"
              style={{ color: ACCENT }}
            >
              Editar la lista
            </button>
            )}
          </div>
          {traeIncluidos.length === 0 ? (
            <p className="text-sm text-zinc-500">
              Esta renta no trae nada configurado: entra a la cotización como un
              renglón normal.
            </p>
          ) : (
            <>
              <ul className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {traeIncluidos.map(({ concepto, conceptoId, cantidad }) => {
                  const pid = proveedorDe(concepto);
                  const prov = pid
                    ? proveedoresPorId[pid]?.nombre || concepto.proveedorNombre
                    : null;
                  return (
                    <li key={conceptoId} className="flex items-center gap-3 py-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/servicios/${conceptoId}`)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <span className="block text-sm font-medium text-zinc-900 dark:text-white truncate hover:underline">
                          {concepto.nombre}
                        </span>
                        <span className="block text-xs text-zinc-500">
                          {prov ? `Lo da ${prov}` : "Del venue"}
                          {llevaInventario(concepto) ? " · con piezas" : ""}
                        </span>
                      </button>
                      <span className="text-xs text-zinc-500 whitespace-nowrap">
                        {cantidad} {unidadDe(concepto)}
                        {cantidad === 1 ? "" : "s"}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-xs text-zinc-500">
                Al cotizar esta renta, todo esto llega marcado y se puede desmarcar
                lo que ese evento no lleve. Lo que quede marcado entra como renglón
                propio en la cotización, sin precio.
              </p>
            </>
          )}
        </div>
      )}

      {/* En qué rentas va incluido este servicio. Contesta la pregunta que el
          badge deja a medias y da el brinco a la renta. */}
      {!esRenta(item) && padres.length > 0 && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm mb-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
            Va incluido en
          </div>
          <div className="flex flex-wrap gap-2">
            {padres.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => navigate(`/dashboard/servicios/${r.id}`)}
                className="rounded-lg border border-zinc-200 dark:border-zinc-700 px-3 py-1.5 text-sm text-zinc-700 dark:text-zinc-300 hover:border-zinc-300"
              >
                {r.nombre}
              </button>
            ))}
          </div>
          {esCobrable(item) && (
            <p className="mt-2 text-xs text-zinc-500">
              También se puede cotizar por separado: va incluido en esas rentas y
              se cobra cuando lo agregas como renglón propio.
            </p>
          )}
        </div>
      )}

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="border-b border-zinc-200 dark:border-zinc-700/60 mb-5">
          <nav className="-mb-px flex gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm font-semibold transition border-b-2 whitespace-nowrap ${
                  tabActivo === t.id
                    ? "text-zinc-900 dark:text-white"
                    : "text-zinc-400 hover:text-zinc-600 border-transparent"
                }`}
                style={tabActivo === t.id ? { borderBottomColor: ACCENT } : undefined}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {tabActivo === "inventario" && conInventario && (
        <ServicioInventario item={item} venueId={selectedVenueId} soloLectura={soloLectura} />
      )}

      {tabActivo === "reservas" && conInventario && (
        <ServicioReservas item={item} venueId={selectedVenueId} />
      )}

      {/* Edición — el formulario compartido (FRA-348) en un dialog propio,
          para que Editar responda desde cualquier pestaña */}
      {isEditing && form && !soloLectura && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsEditing(false)} />
          <div className="relative w-full max-w-2xl rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[92vh]">
            <div className="flex items-start gap-2 px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
              <div>
                <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
                  Editar {item.nombre}
                </h2>
                <p className="mt-0.5 text-xs text-zinc-500">
                  Cambia cómo se cobra, quién lo da y qué lleva. El historial
                  y las piezas no se tocan.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="ml-auto text-zinc-400 hover:text-zinc-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <ServicioForm
                form={form}
                setForm={setForm}
                proveedores={proveedores}
                modo="edicion"
                itemId={servicioId}
              />
            </div>
            <div className="flex items-center gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
              <button
                type="button"
                onClick={() => setShowDelete(true)}
                className="text-xs font-medium text-red-500 hover:text-red-700"
              >
                Eliminar del catálogo
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="ml-auto px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                {saving ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variantes: mismo servicio, precio distinto según la condición */}
      {tabActivo === "resumen" && variantes.length > 0 && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm mb-5">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500 mb-3">
            Variantes
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-xs text-zinc-400">
                  <th className="px-2 py-1 text-left font-medium">Variante</th>
                  <th className="px-2 py-1 text-right font-medium">Cobras</th>
                  {proveedorId && (
                    <th className="px-2 py-1 text-right font-medium">Te cuesta</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {variantes.map((v) => {
                  const cobroV = precioDeVariante(item, v);
                  const costoV = proveedorId ? costoProveedorDeVariante(item, v) : null;
                  return (
                    <tr key={v.id}>
                      <td className="px-2 py-2 text-zinc-900 dark:text-white">
                        {v.nombre || "Sin nombre"}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {cobroV > 0 ? (
                          <>
                            <span className="font-medium text-zinc-900 dark:text-white">
                              {fmtMoney(cobroV)}
                            </span>
                            <span className="text-xs text-zinc-400"> /{unidadDe(item)}</span>
                          </>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </td>
                      {proveedorId && (
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {costoV > 0 ? (
                            <>
                              <span className="font-medium text-red-600">
                                {fmtMoney(costoV)}
                              </span>
                              <span className="text-xs text-zinc-400"> /{unidadDe(item)}</span>
                            </>
                          ) : (
                            <span className="text-zinc-300">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cobrado por variante: "¿cuánto vendí de cada una?" */}
      {tabActivo === "resumen" && ingresosPorVariante.length > 1 && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm mb-5">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500 mb-3">
            Cobrado por variante
          </div>
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-700/40 text-sm">
            {ingresosPorVariante.map((v) => (
              <li key={v.varianteId || "_base"} className="flex items-center justify-between py-1.5">
                <span className="text-zinc-900 dark:text-white">
                  {v.nombre}
                  <span className="ml-1.5 text-xs text-zinc-400">
                    {v.lineas} {v.lineas === 1 ? "línea" : "líneas"}
                  </span>
                </span>
                <span className="font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                  {fmtMoney(v.total)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Historial del costo del proveedor: con qué frecuencia y cuánto
          sube lo que te cobra — componente único (FRA-340). Vive en el
          servicio, no en el proveedor: un proveedor da varios servicios con
          precios distintos. */}
      {tabActivo === "resumen" && proveedorId && (
        <HistorialPrecios
          pathSegments={["venues", selectedVenueId, "conceptos", item.id, "tarifaHistorial"]}
          titulo={`Historial del costo · ${proveedorNombre}`}
          labels={{ inicial: "Costo inicial", eliminado: "Sin costo de proveedor" }}
          ocultarVacio
          limite={20}
          nota="Es lo que el proveedor te cobra por este servicio (tu costo), no el precio al cliente. Los costos de eventos pasados conservan el precio vigente en su momento."
          className="mb-5"
        />
      )}

      {/* Capturar la inversión si todavía no está. Solo tiene sentido en lo
          que el venue compró: lo que da un proveedor no se adquiere. */}
      {tabActivo === "resumen" && !soloLectura && !adquisicion && !esDescuento(item) && esDelVenue(item) && (
        <div className="rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 p-5 mb-5">
          {inversion ? (
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">
                Costo de adquisición
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={fmtMXNInput(inversion.monto)}
                    onChange={(e) =>
                      setInversion({ ...inversion, monto: normalizeMoneyInput(e.target.value) })
                    }
                    placeholder="0"
                    className={`${inputClass} pl-7`}
                  />
                </div>
                <input
                  type="date"
                  value={inversion.fecha}
                  onChange={(e) => setInversion({ ...inversion, fecha: e.target.value })}
                  className={inputClass}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                <input
                  type="checkbox"
                  checked={inversion.registrarGasto}
                  onChange={(e) =>
                    setInversion({ ...inversion, registrarGasto: e.target.checked })
                  }
                />
                Registrar la compra como gasto en Cuentas
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleGuardarInversion}
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
                  style={{ backgroundColor: ACCENT }}
                >
                  Guardar
                </button>
                <button
                  type="button"
                  onClick={() => setInversion(null)}
                  className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-sm text-zinc-600 dark:text-zinc-300">
                Sin costo de adquisición registrado.
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Si lo compraste, con ese dato se calcula cuánto de la inversión
                llevas recuperado y en qué evento alcanzó el punto de equilibrio.
              </p>
              <button
                type="button"
                onClick={() =>
                  setInversion({
                    monto: "",
                    fecha: todayInputValue(),
                    registrarGasto: false,
                  })
                }
                className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm"
                style={{ backgroundColor: ACCENT }}
              >
                Registrar costo de adquisición
              </button>
            </div>
          )}
        </div>
      )}

      {/* Recuperación de la inversión */}
      {tabActivo === "resumen" && recuperacion && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm mb-5">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500 mb-3">
            Recuperación de la inversión
          </div>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <div>
              <div className="text-2xl font-bold text-zinc-900 dark:text-white">
                {fmtMoney(recuperacion.recuperado)}
                <span className="text-base font-normal text-zinc-400">
                  {" "}de {fmtMoney(recuperacion.inversion)}
                </span>
              </div>
              <div className="text-xs text-zinc-500">
                Cobrado {fmtMoney(recuperacion.cobrado)} en {recuperacion.eventos}{" "}
                {recuperacion.eventos === 1 ? "evento" : "eventos"}
                {recuperacion.costosDeUso > 0
                  ? ` · menos ${fmtMoney(recuperacion.costosDeUso)} que costó usarlo`
                  : ""}
              </div>
            </div>
            {recuperacion.pagadoEn ? (
              <div className="text-sm font-semibold text-emerald-600">
                Inversión recuperada
                {recuperacion.meses != null
                  ? ` en ${recuperacion.meses} ${recuperacion.meses === 1 ? "mes" : "meses"}`
                  : ""}
              </div>
            ) : (
              <div className="text-sm font-semibold text-amber-600">
                Faltan {fmtMoney(recuperacion.falta)}
              </div>
            )}
          </div>
          <div className="mt-3 h-2 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
            <div
              className={`h-full rounded-full ${
                recuperacion.pagadoEn ? "bg-emerald-500" : "bg-amber-500"
              }`}
              style={{ width: `${Math.round(recuperacion.pct * 100)}%` }}
            />
          </div>
          {!esCobrable(item) && (
            <p className="mt-2 text-xs text-amber-600">
              Marcado como no cobrable, así que no genera ingresos que
              puedan recuperar la inversión.
            </p>
          )}
          {recuperacion.pagadoEn && (
            <p className="mt-2 text-xs text-zinc-500">
              Alcanzó el punto de equilibrio en{" "}
              <button
                type="button"
                onClick={() => navigate(`/dashboard/eventos/${recuperacion.pagadoEn.eventId}`)}
                className="hover:underline font-medium"
                style={{ color: ACCENT }}
              >
                {recuperacion.pagadoEn.nombre}
              </button>
              . A partir de ese evento, lo que genera es utilidad.
            </p>
          )}
          <p className="mt-2 text-xs text-zinc-400">
            Se compara la inversión contra lo cobrado en las líneas de
            cotización que salen de él (sin IVA), menos los costos de
            operarlo. El costo de adquisición no cuenta como costo de
            operación: es justamente lo que se está recuperando.
          </p>
        </div>
      )}

      {/* Adquisición registrada (referencia) */}
      {tabActivo === "resumen" && adquisicion && !recuperacion && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm mb-5 text-sm text-zinc-500">
          Adquisición registrada: {fmtMoney(adquisicion.monto)}.
        </div>
      )}

      {/* Costos — a un descuento no se le registran gastos */}
      {tabActivo === "resumen" && !esDescuento(item) && (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs uppercase tracking-wide font-semibold text-zinc-500">
            Costos registrados
          </span>
          {!gasto && !soloLectura && (
            <button
              type="button"
              onClick={() =>
                setGasto({
                  concepto: "",
                  monto: "",
                  fecha: todayInputValue(),
                  categoria: "Mantenimiento",
                  estado: "pagado",
                  metodo: "",
                })
              }
              className="ml-auto px-2.5 py-1 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              Registrar gasto
            </button>
          )}
        </div>

        {gasto && (
          <div className="mb-4 rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Concepto
                </label>
                <input
                  type="text"
                  value={gasto.concepto}
                  onChange={(e) => setGasto({ ...gasto, concepto: e.target.value })}
                  placeholder="Mantenimiento, reparación, limpieza…"
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Monto</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={fmtMXNInput(gasto.monto)}
                    onChange={(e) => setGasto({ ...gasto, monto: normalizeMoneyInput(e.target.value) })}
                    placeholder="0"
                    className={`${inputClass} pl-7`}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Fecha</label>
                <input
                  type="date"
                  value={gasto.fecha}
                  onChange={(e) => setGasto({ ...gasto, fecha: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Categoría</label>
                <select
                  value={gasto.categoria}
                  onChange={(e) => setGasto({ ...gasto, categoria: e.target.value })}
                  className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
                >
                  {CATEGORIAS_COSTO.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="flex rounded-lg border border-zinc-300 dark:border-zinc-600 overflow-hidden">
                {[
                  { id: "pagado", label: "Pagado" },
                  { id: "por_pagar", label: "Por pagar" },
                ].map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setGasto({ ...gasto, estado: e.id })}
                    className={`px-3 py-2 text-sm font-medium transition ${
                      gasto.estado === e.id
                        ? "text-white"
                        : "bg-white dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-50"
                    }`}
                    style={gasto.estado === e.id ? { backgroundColor: ACCENT } : undefined}
                  >
                    {e.label}
                  </button>
                ))}
              </div>
              {gasto.estado === "pagado" && (
                <MetodoPagoField
                  value={gasto.metodo || ""}
                  onChange={(v) => setGasto({ ...gasto, metodo: v })}
                  metodosPago={metodosPago}
                  className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
                  vacio="Método"
                />
              )}
              <button
                type="button"
                onClick={handleRegistrarGasto}
                disabled={savingGasto}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                {savingGasto ? "Guardando…" : "Guardar gasto"}
              </button>
              <button
                type="button"
                onClick={() => setGasto(null)}
                className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
              >
                Cancelar
              </button>
            </div>
            <p className="mt-2 text-xs text-zinc-500">
              Queda ligado a este servicio y aparece en Cuentas como gasto del
              venue. Si el servicio tiene inversión registrada, cuenta como
              costo de uso en su recuperación.
            </p>
          </div>
        )}

        {costosDelItem.length === 0 ? (
          <p className="text-sm text-zinc-400">
            Todavía no hay costos ligados a este servicio. Registra aquí sus
            gastos (mantenimiento, reparaciones) para que cuenten en sus
            números.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {costosOrdenados.map((c) => {
                  const ev = c.eventId ? eventsById[c.eventId] : null;
                  return (
                    <tr key={c.id}>
                      <td className="px-2 py-2 text-zinc-500 whitespace-nowrap w-32">
                        {formatCalendarDate(c.fecha, calendarTimezone, { style: "short" }) || "—"}
                      </td>
                      <td className="px-2 py-2 text-zinc-900 dark:text-white">
                        {c.concepto || "—"}
                        <div className="text-xs text-zinc-400">
                          {(c.estado || "por_pagar") === "pagado"
                            ? [
                                `Pagado el ${formatCalendarDate(c.fechaPago || c.fecha, calendarTimezone, { style: "short" }) || "—"}`,
                                c.metodo,
                              ]
                                .filter(Boolean)
                                .join(" · ")
                            : "Por pagar"}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        {ev ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/dashboard/eventos/${ev.id}`)}
                            className="text-sm hover:underline"
                            style={{ color: ACCENT }}
                          >
                            {ev.nombre || "Sin nombre"}
                          </button>
                        ) : (
                          <span className="text-xs text-zinc-400">Gasto general</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                        {fmtMoney(c.monto)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      <Alert open={showDelete && !soloLectura} onClose={() => setShowDelete(false)}>
        <AlertTitle>¿Eliminar este servicio?</AlertTitle>
        <AlertDescription>
          Los costos y las cotizaciones que ya lo usan se conservan, pero
          dejarán de tener a qué apuntar. Si solo quieres dejar de usarlo,
          archívalo.
        </AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setShowDelete(false)}>Cancelar</Button>
          <Button color="red" onClick={handleDelete}>Eliminar</Button>
        </AlertActions>
      </Alert>
    </div>
  );
};

export default ServicioDetallePage;
